import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';

import ActionLink from '../builder/ActionLink';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import Checkbox from '../builder/Checkbox';
import ColorPicker from '../builder/ColorPicker';
import ImageUploader from '../builder/ImageUploader';
import Input from '../builder/Input';
import MediaUploader from '../builder/MediaUploader';
import Message from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select';
import UrlField from '../builder/UrlField';

import StoryStage from '../front/stages/StoryStage';

const StoryStageBuilder = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const video_url = (stage.main_video) ? stage.main_video.url : null;

	const padding_options = [
		{ k:'0', v:i18n("None") },
		{ k:'small', v:i18n("Small") },
		{ k:'normal', v:i18n("Normal") },
		{ k:'big', v:i18n("Big") },
	];

	const button_options = [
		{ k:'thankyou', v:i18n("Thank you page") + " (" + i18n("default") + ")" },
		{ k:'external', v:i18n("An external URL") },
	];

	const isYouTubeVideo = (video_url) => {
		return ((video_url?.type ?? 'youtube') == 'youtube') ? true : false;
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<ColorPicker
							_class={{ size:60, first:true }}
							name="bg_color"
							_hide_code={true}
							color={stage.bg_color}
						>Background color</ColorPicker>

						<ImageUploader
							name="bg_image"
							image={stage.full_bg_image}
							_size={{ width:1080, height:1920 }}
							small_input={true}
							display_custom_dragging_instructions="Upload image"
						>Background image</ImageUploader>

						<MediaUploader
							imageField="main_image"
							videoField="main_video"
							image={stage.full_main_image}
							video={video_url}
						>Upload media</MediaUploader>

						{(isNotEmpty(stage, 'main_video') && isYouTubeVideo(stage.main_video)) && (
							<Checkbox
								name='main_video_portrait'
							>Is portrait?</Checkbox>
						)}

						{((stage?.main_video_portrait ?? false) === false) && (
							<RichEditor
								name="main_description"
								has_emojis={true}
							>Textual content</RichEditor>
						)}

						{(isNotEmpty(stage, 'main_description')) && (
							<ColorPicker
								_class={{size:60}}
								name="text_color"
								_hide_code={true}
								color={stage?.text_color ?? '#FFFFFF'}
							>Text color</ColorPicker>
						)}

						{(isNotEmpty(stage, 'main_description')) && (
							<ColorPicker
								_class={{size:60}}
								name="highlight_color"
								_hide_code={true}
								color={stage?.highlight_color ?? '#000000'}
							>Highlight color</ColorPicker>
						)}

						<ColorPicker
							_class={{size:60}}
							name="button_color"
							_hide_code={true}
							color={stage?.button_color ?? '#FFFFFF'}
						>Button color</ColorPicker>

						<Select
							name="button_target"
							values={button_options}
						>Where does the continue button target?</Select>

						{(stage.button_target != undefined && stage.button_target == 'external') && (
							<UrlField
								name="button_url"
							>External URL</UrlField>
						)}

						<Input
							name="button_label"
							_class={{size:50}}
						>Button label</Input>

						{(isNotEmpty(stage, 'main_description') || isNotEmpty(stage, 'main_image') || isNotEmpty(stage, 'main_video')) && (
							<Select
								name="bg_padding"
								_class={{size:50}}
								value={(stage.bg_padding != undefined) ? stage.bg_padding : 'normal'}
								values={padding_options}
							>External padding</Select>
						)}

						<Checkbox
							name="direct_points"
						>Give points when completing this participation stage</Checkbox>

						{(stage.direct_points != undefined && stage.direct_points) && (
							<Input
								name="points"
								placeholder="0"
								_class={{size:50}}
							>How many points?</Input>
						)}
					</Model>

					<Message
						_class={{ other:'in_bottom' }}
					>
						<p><FormattedMessage id="If you give direct prizes for the fact of registering a participation remember to configure them from the {link_prizes} menu." values={{ link_prizes:<ActionLink _href="#prizes">Prizes</ActionLink> }} /></p>
					</Message>
				</article>
			</BuilderSidebar>

			<BuilderCanvas is_adaptable={true} fit_to_window="content">
				<StoryStage
					stage={stage}
					is_builder={true}
				/>
			</BuilderCanvas>
		</BuilderMain>
	);
}

export default injectIntl(StoryStageBuilder);